import React from "react";
import ReportTable from "../ReportTable";
import GroupedHeaderTable from "../GroupedHeaderTable";
import {
  getNumberOrNA,
  principleTitles,
  renderYesNo,
  safeText,
} from "./helpers";
import usePrincipleData from "./usePrincipleData";

const Principle1 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(1);
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};

  const training = essential?.percentageCoverageByTraining || {};
  const trainingRows = [
    ["boardOfDirectors", "Board of Directors"],
    ["keyManagementPersonnel", "Key Management Personnel"],
    ["employeesOtherThanBoDAndKMPs", "Employees other than BoD and KMPs"],
    ["workers", "Workers"],
  ].map(([key, label], idx) => ({
    _id: `p1-tr-${idx}`,
    segment: label,
    totalProgrammes: safeText(
      training?.[key]?.awarenessProgramHeld ??
        training?.[key]?.totalProgrammesHeld,
      "-",
    ),
    topics: Array.isArray(training?.[key]?.principleUnderTraining)
      ? training[key].principleUnderTraining.join(", ")
      : "-",
    coverage: safeText(
      training?.[key]?.categoryCoveredPercentage ??
        training?.[key]?.percentageCovered,
      "-",
    ),
  }));

  const conflict = essential?.conflictOfInterestComplaints || {};
  const complaintRows = [
    ["Directors", "directors"],
    ["KMPs", "keyManagement"],
  ].map(([label, key], idx) => ({
    _id: `p1-comp-${idx}`,
    segment: `Number of complaints received in relation to issues of Conflict of Interest of the ${label}`,
    currentNumber: getNumberOrNA(
      conflict?.currentFinancialYear?.[key]?.numberOfComplaints,
    ),
    currentRemarks: safeText(
      conflict?.currentFinancialYear?.[key]?.remarks,
      "N/A",
    ),
    previousNumber: getNumberOrNA(
      conflict?.previousFinancialYear?.[key]?.numberOfComplaints,
    ),
    previousRemarks: safeText(
      conflict?.previousFinancialYear?.[key]?.remarks,
      "N/A",
    ),
  }));

  const leadershipRows = (
    Array.isArray(leadership?.awarenessProgram)
      ? leadership.awarenessProgram
      : []
  ).map((row, idx) => ({
    _id: `p1-l-${idx}`,
    totalProgram: safeText(row?.totalProgram, "N/A"),
    topicsPrinciples: Array.isArray(row?.topicsPrinciples)
      ? row.topicsPrinciples
          .filter((item) => String(item).trim().toLowerCase() !== "select all")
          .join(", ")
      : safeText(row?.topicsPrinciples, "N/A"),
    percentage: safeText(row?.percentage, "N/A"),
  }));

  const finesRows = [
    {
      _id: "p1-fine-m-1",
      type: "Monetary - Penalty / Fine",
      ngrbc: safeText(essential?.monetary?.penaltyFine?.ngrbcPrinciple, "N/A"),
      agency: safeText(
        essential?.monetary?.penaltyFine?.regulatoryAgency,
        "N/A",
      ),
      amount: safeText(essential?.monetary?.penaltyFine?.amountInINR, "N/A"),
      caseBrief: safeText(essential?.monetary?.penaltyFine?.caseBrief, "N/A"),
      appeal: renderYesNo(essential?.monetary?.penaltyFine?.hasAppealPreferred),
    },
    {
      _id: "p1-fine-m-2",
      type: "Monetary - Settlement",
      ngrbc: safeText(essential?.monetary?.settlement?.ngrbcPrinciple, "N/A"),
      agency: safeText(
        essential?.monetary?.settlement?.regulatoryAgency,
        "N/A",
      ),
      amount: safeText(essential?.monetary?.settlement?.amountInINR, "N/A"),
      caseBrief: safeText(essential?.monetary?.settlement?.caseBrief, "N/A"),
      appeal: renderYesNo(essential?.monetary?.settlement?.hasAppealPreferred),
    },
    {
      _id: "p1-fine-m-3",
      type: "Monetary - Compounding fee",
      ngrbc: safeText(
        essential?.monetary?.compoundingFee?.ngrbcPrinciple,
        "N/A",
      ),
      agency: safeText(
        essential?.monetary?.compoundingFee?.regulatoryAgency,
        "N/A",
      ),
      amount: safeText(essential?.monetary?.compoundingFee?.amountInINR, "N/A"),
      caseBrief: safeText(
        essential?.monetary?.compoundingFee?.caseBrief,
        "N/A",
      ),
      appeal: renderYesNo(
        essential?.monetary?.compoundingFee?.hasAppealPreferred,
      ),
    },
    {
      _id: "p1-fine-nm-1",
      type: "Non-Monetary - Imprisonment",
      ngrbc: safeText(
        essential?.nonMonetary?.imprisonment?.ngrbcPrinciple,
        "N/A",
      ),
      agency: safeText(
        essential?.nonMonetary?.imprisonment?.regulatoryAgency,
        "N/A",
      ),
      amount: "-",
      caseBrief: safeText(
        essential?.nonMonetary?.imprisonment?.caseBrief,
        "N/A",
      ),
      appeal: renderYesNo(
        essential?.nonMonetary?.imprisonment?.hasAppealPreferred,
      ),
    },
    {
      _id: "p1-fine-nm-2",
      type: "Non-Monetary - Punishment",
      ngrbc: safeText(
        essential?.nonMonetary?.punishment?.ngrbcPrinciple,
        "N/A",
      ),
      agency: safeText(
        essential?.nonMonetary?.punishment?.regulatoryAgency,
        "N/A",
      ),
      amount: "-",
      caseBrief: safeText(essential?.nonMonetary?.punishment?.caseBrief, "N/A"),
      appeal: renderYesNo(
        essential?.nonMonetary?.punishment?.hasAppealPreferred,
      ),
    },
  ];
  const monetaryRows = finesRows.filter((row) =>
    String(row?.type).startsWith("Monetary"),
  );
  const nonMonetaryRows = finesRows.filter((row) =>
    String(row?.type).startsWith("Non-Monetary"),
  );

  const appealRows = (
    Array.isArray(essential?.appealDetails) ? essential.appealDetails : []
  ).map((row, idx) => ({
    _id: row?._id || `p1-app-${idx}`,
    authority: safeText(row?.regulatoryEnforcement, "N/A"),
    caseDetails: safeText(row?.caseDetails, "N/A"),
  }));

  const disciplinaryRows = [
    ["Board of Directors", "boardOfDirectors"],
    ["Key Management Personnel", "keyManagementPersonnel"],
    ["Employees other than BoD and KMPs", "employeesOtherThanBoDAndKMPs"],
    ["Workers", "workers"],
  ].map(([segment, key], idx) => ({
    _id: `p1-disc-${idx}`,
    segment,
    current: getNumberOrNA(
      essential?.disciplinaryActions?.[key]?.currentYear ??
        essential?.disciplinaryActions?.[key],
    ),
    previous: getNumberOrNA(
      essential?.disciplinaryActions?.[key]?.previousYear,
    ),
  }));

  return (
    <div className={styles.principleBlock}>
      <div
        className={styles.principleHeading}
      >{`PRINCIPLE 1: ${principleTitles[0]}`}</div>
      {loading ? (
        <div className={styles.meta}>Loading principle 1...</div>
      ) : null}
      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>
      <div className={styles.question}>
        1. Percentage coverage by training & awareness programs on any of the
        Principles during the financial year:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "segment", label: "Segment" },
          {
            field: "totalProgrammes",
            label: "Total number of training and awareness programmes held",
          },
          {
            field: "topics",
            label:
              "Topics / principles covered under the training and its impact",
          },
          {
            field: "coverage",
            label:
              "% age of persons in respective category covered by the awareness programmes",
          },
        ]}
        rows={trainingRows}
      />
      <div className={styles.question}>
        2. Details of fines/ penalties/ punishment/ award/ compounding fees/
        settlement amount paid in proceedings (by the entity or by directors/
        KMPs) with regulators/ law enforcement agencies/ judicial institutions,
        in the financial year, in the following format:
      </div>
      <div className={styles.answer}>
        (Note: The entity shall make disclosures on the basis of materiality as
        specified in Regulation 30 of SEBI (Listing Obligations and Disclosure
        Obligations) Regulations, 2015 and as disclosed on the entity's
        website):
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.detailsOfFine)}
      </div>
      {essential?.detailsOfFine ? (
        <>
          <div className={styles.answer}>Monetary</div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "type", label: "Type" },
              { field: "ngrbc", label: "NGRBC Principle" },
              { field: "agency", label: "Regulatory Agency" },
              { field: "amount", label: "Amount (In INR)" },
              { field: "caseBrief", label: "Brief of the case" },
              { field: "appeal", label: "Has an appeal been preferred?" },
            ]}
            rows={monetaryRows}
          />
          <div className={styles.answer}>Non-Monetary</div>
          <ReportTable
            showSerialNumber={false}
            columns={[
              { field: "type", label: "Type" },
              { field: "ngrbc", label: "NGRBC Principle" },
              { field: "agency", label: "Regulatory Agency" },
              { field: "amount", label: "Amount (In INR)" },
              { field: "caseBrief", label: "Case Brief" },
              { field: "appeal", label: "Has an appeal been preferred?" },
            ]}
            rows={nonMonetaryRows}
          />
        </>
      ) : null}

      <div className={styles.question}>
        3. Of the instances disclosed in Question 2 above, details of the
        Appeal/ Revision preferred in cases where monetary or non-monetary
        action has been appealed.
      </div>
      {essential?.detailsOfFine ? (
        <ReportTable
          showSerialNumber={false}
          columns={[
            { field: "authority", label: "Name of regulatory/enforcement" },
            { field: "caseDetails", label: "Case details" },
          ]}
          rows={appealRows}
        />
      ) : (
        <div className={styles.answer}>Not Applicable</div>
      )}

      <div className={styles.question}>
        4. Does the entity have an anti-corruption or anti-bribery policy? If
        yes, provide details in brief and if available, provide a web-link to
        the policy.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.antiCorruptionPolicy?.hasPolicy)}
        {essential?.antiCorruptionPolicy?.hasPolicy
          ? `, ${safeText(essential?.antiCorruptionPolicy?.policyDetails, "N/A")} (${safeText(
              essential?.antiCorruptionPolicy?.policyLink,
              "N/A",
            )})`
          : ""}
      </div>

      <div className={styles.question}>
        5. Number of Directors/ KMPs/ employees/ workers against whom
        disciplinary action was taken by any law enforcement agency for the
        charge of bribery/corruption :
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "segment", label: "Segment" },
          { field: "current", label: "Current Financial Year" },
          { field: "previous", label: "Previous Financial Year" },
        ]}
        rows={disciplinaryRows}
      />
      <div className={styles.question}>
        6. Details of complaints with regard to conflict of interest:
      </div>
      <GroupedHeaderTable
        leftColumn={{ field: "segment", label: "Segment" }}
        groupedColumns={[
          {
            label: "Current Financial Year",
            columns: [
              { field: "currentNumber", label: "Number" },
              { field: "currentRemarks", label: "Remarks" },
            ],
          },
          {
            label: "Previous Financial Year",
            columns: [
              { field: "previousNumber", label: "Number" },
              { field: "previousRemarks", label: "Remarks" },
            ],
          },
        ]}
        rows={complaintRows}
      />
      <div className={styles.question}>
        7. Provide details of any corrective action taken or underway on issues
        related to fines/ penalties/ action taken by regulators/ law enforcement
        agencies/ judicial institutions, on cases of corruption and conflicts of
        interest.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.correctiveActions, "N/A")}
      </div>
      <div className={styles.question}>
        8. Number of days of accounts payables [(Accounts payable*365)/ Cost of
        goods/services procured] in the following format:
      </div>
      <div className={styles.answer}>{`Current Financial Year: ${getNumberOrNA(
        essential?.accountsPayables?.currentFinancialYear,
      )}, Previous Financial Year: ${getNumberOrNA(essential?.accountsPayables?.previousFinancialYear)}`}</div>

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>
      <div className={styles.question}>
        1. Awareness programmes conducted for value chain partners on any of the
        Principles during the financial year:
      </div>
      <ReportTable
        columns={[
          {
            field: "totalProgram",
            label: "Total No. of Awareness Programs held",
          },
          {
            field: "topicsPrinciples",
            label: "Topics / principles covered under the training",
          },
          {
            field: "percentage",
            label:
              "%age of value chain partners covered (by value of business done with such partners) under the awareness programmes",
          },
        ]}
        rows={leadershipRows}
      />
      <div className={styles.question}>
        2. Does the entity have processes in place to avoid / manage conflict of
        interests involving members of the Board? If Yes, provide details of the
        same.
      </div>
      <div className={styles.answer}>
        {renderYesNo(leadership?.avoidManageConflict?.status)}
        {leadership?.avoidManageConflict?.remark ? (
          <>
            <br />
            {safeText(leadership?.avoidManageConflict?.remark, "")}
          </>
        ) : null}
      </div>
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle1;
